import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { apiClient } from '@api'
import { Button, Input } from '@components/common'
import './Activation.css'

/**
 * Company Activation Page
 *
 * Public page for company onboarding activation workflow:
 * 1. Validate activation token from email link
 * 2. Display password setup form
 * 3. Complete activation and redirect to login
 *
 * Security Features:
 * - Token validation with expiry check
 * - Real-time password strength validation
 * - One-time token use enforcement
 * - Automatic redirect after success
 */
const Activation = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')

  // Step states: validating | set-password | success | error
  const [step, setStep] = useState('validating')
  const [companyData, setCompanyData] = useState(null)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [passwordErrors, setPasswordErrors] = useState([])

  // Validate token on mount
  useEffect(() => {
    validateToken()
  }, [token])

  /**
   * Validate activation token with backend
   */
  const validateToken = async () => {
    if (!token) {
      setStep('error')
      setError('Invalid activation link. Please check your email and try again.')
      return
    }

    try {
      const response = await apiClient.post('/company-activation/validate-token', { token })

      // apiClient returns JSON directly, check for valid flag
      if (response.valid) {
        setCompanyData(response)
        setStep('set-password')
      } else {
        setStep('error')
        setError('Invalid activation link.')
      }
    } catch (err) {
      handleTokenValidationError(err)
    }
  }

  /**
   * Handle token validation errors
   */
  const handleTokenValidationError = (err) => {
    if (err.response?.status === 410) {
      // Token expired or used
      if (err.response.data.expired) {
        setStep('error')
        setError(
          `This activation link has expired. Please contact your administrator to resend the activation email for ${err.response.data.company_name || 'your company'}.`
        )
      } else {
        setStep('error')
        setError(err.response.data.message || 'Activation link has already been used.')
      }
    } else if (err.response?.status === 409) {
      // Already activated
      setStep('error')
      setError('This company is already activated. Please login to continue.')
    } else {
      setStep('error')
      setError('Invalid activation link. Please check the URL and try again.')
    }
  }

  /**
   * Validate password strength
   * Returns array of missing requirements
   */
  const validatePassword = (pwd) => {
    const errors = []

    if (pwd.length < 8) {
      errors.push('At least 8 characters')
    }
    if (!/[A-Z]/.test(pwd)) {
      errors.push('One uppercase letter')
    }
    if (!/[a-z]/.test(pwd)) {
      errors.push('One lowercase letter')
    }
    if (!/[0-9]/.test(pwd)) {
      errors.push('One number')
    }
    if (!/[^A-Za-z0-9]/.test(pwd)) {
      errors.push('One special character (!@#$%^&*)')
    }

    return errors
  }

  /**
   * Handle password input change with validation
   */
  const handlePasswordChange = (e) => {
    const pwd = e.target.value
    setPassword(pwd)
    setPasswordErrors(validatePassword(pwd))
  }

  /**
   * Submit activation form
   */
  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    // Validate password strength
    const errors = validatePassword(password)
    if (errors.length > 0) {
      setError('Password does not meet security requirements')
      return
    }

    // Validate terms agreement
    if (!agreedToTerms) {
      setError('You must agree to the Terms & Conditions to continue')
      return
    }

    try {
      setLoading(true)

      const response = await apiClient.post('/company-activation/complete', {
        token,
        password,
      })

      // apiClient returns JSON directly, check for success flag
      if (response.success) {
        setStep('success')

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login')
        }, 3000)
      } else {
        setError(response.message || 'Failed to activate account. Please try again.')
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to activate account. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Render validating state
  if (step === 'validating') {
    return (
      <div className="activation-container">
        <div className="activation-card">
          <div className="activation-spinner"></div>
          <p className="activation-loading-text">Validating activation link...</p>
        </div>
      </div>
    )
  }

  // Render error state
  if (step === 'error') {
    return (
      <div className="activation-container">
        <div className="activation-card activation-card--error">
          <div className="activation-icon activation-icon--error">✖</div>
          <h1 className="activation-title">Activation Failed</h1>
          <p className="activation-message">{error}</p>
          <div className="activation-actions">
            <Button onClick={() => navigate('/login')} variant="primary">
              Go to Login
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // Render success state
  if (step === 'success') {
    return (
      <div className="activation-container">
        <div className="activation-card activation-card--success">
          <div className="activation-icon activation-icon--success">✓</div>
          <h1 className="activation-title">Account Activated!</h1>
          <p className="activation-message">
            Your company account has been successfully activated.
          </p>
          <p className="activation-redirect-text">
            You will be redirected to login in 3 seconds...
          </p>
          <div className="activation-actions">
            <Button onClick={() => navigate('/login')} variant="primary">
              Go to Login Now
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // Render password setup form
  return (
    <div className="activation-container">
      <div className="activation-card">
        <div className="activation-header">
          <h1 className="activation-title">Activate Your Account</h1>
          <p className="activation-company-name">{companyData?.company?.name}</p>
          <p className="activation-welcome-text">
            Welcome, {companyData?.manager?.first_name}! Please create a strong password to
            complete activation.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="activation-form">
          <div className="activation-form-field">
            <Input
              type="password"
              label="Password"
              value={password}
              onChange={handlePasswordChange}
              placeholder="Enter your password"
              required
              autoFocus
            />
          </div>

          {/* Password strength indicator */}
          {password && (
            <div className="activation-password-requirements">
              <p className="activation-requirements-title">Password must include:</p>
              <ul className="activation-requirements-list">
                <li
                  className={
                    passwordErrors.includes('At least 8 characters')
                      ? 'activation-requirement-invalid'
                      : 'activation-requirement-valid'
                  }
                >
                  {passwordErrors.includes('At least 8 characters') ? '○' : '✓'} At least 8
                  characters
                </li>
                <li
                  className={
                    passwordErrors.includes('One uppercase letter')
                      ? 'activation-requirement-invalid'
                      : 'activation-requirement-valid'
                  }
                >
                  {passwordErrors.includes('One uppercase letter') ? '○' : '✓'} One uppercase letter
                </li>
                <li
                  className={
                    passwordErrors.includes('One lowercase letter')
                      ? 'activation-requirement-invalid'
                      : 'activation-requirement-valid'
                  }
                >
                  {passwordErrors.includes('One lowercase letter') ? '○' : '✓'} One lowercase letter
                </li>
                <li
                  className={
                    passwordErrors.includes('One number')
                      ? 'activation-requirement-invalid'
                      : 'activation-requirement-valid'
                  }
                >
                  {passwordErrors.includes('One number') ? '○' : '✓'} One number
                </li>
                <li
                  className={
                    passwordErrors.includes('One special character (!@#$%^&*)')
                      ? 'activation-requirement-invalid'
                      : 'activation-requirement-valid'
                  }
                >
                  {passwordErrors.includes('One special character (!@#$%^&*)') ? '○' : '✓'} One
                  special character (!@#$%^&*)
                </li>
              </ul>
            </div>
          )}

          <div className="activation-form-field">
            <Input
              type="password"
              label="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter your password"
              required
            />
          </div>

          {/* Terms & Conditions Checkbox */}
          <div className="activation-terms">
            <label className="activation-terms-label">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="activation-terms-checkbox"
              />
              <span className="activation-terms-text">
                I agree to the{' '}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="activation-terms-link"
                >
                  Terms & Conditions
                </a>{' '}
                and{' '}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="activation-terms-link"
                >
                  Privacy Policy
                </a>
              </span>
            </label>
          </div>

          {error && <div className="activation-error-message">{error}</div>}

          <Button
            type="submit"
            variant="primary"
            disabled={loading || passwordErrors.length > 0 || !agreedToTerms}
            fullWidth
          >
            {loading ? 'Activating...' : 'Activate Account'}
          </Button>

          <div className="activation-security-notice">
            🔒 Your password is encrypted and stored securely.
          </div>
        </form>
      </div>
    </div>
  )
}

export default Activation
